import { ReactElement } from "react";
import { AdminOnly } from "../../../../components/authFilter/AdminOnly";
import { BadgeZinc } from "../../../../components/badge/Badge";
import { usePaymentTermsIndicatorDirect } from "../../../../components/paymentTypeIndicator/usePaymentTermsIndicatorDirect";
import { TooltipContainer } from "@sellar-io/components";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";
import { OrderInitiatorIndicator } from "../../OrderInitiatorIndicator";
import { OrderCheckoutSourceIndicator } from "../../OrderCheckoutSourceIndicator";
import OrderStatusIndicator from "../../orderStatusIndicator/OrderStatusIndicator";
import PaymentStatusIndicator from "../../paymentStatusIndicator/PaymentStatusIndicator";
import { formatOrderListDate } from "./orderListElementHelpers";
import { OrderListElementLayout } from "./OrderListElementLayout";
import { OrderListElementProps, OrderListOrder } from "./orderListElementTypes";

export const BuyerOrderListElement = ({
  order,
  selectedOrderId,
  showFulfillment = true,
  showPayment = true,
  showStatus = true,
}: OrderListElementProps): ReactElement => {
  const showSellarCreditFeatures = order.paymentType === "sellarCredit";
  const { full: sellarCreditIcon } = usePaymentTermsIndicatorDirect({
    indicatorType: "sellarCredit",
    tooltipContent: "Sellar Credit",
    overrideText: "",
  });
  const isImplicitProformaOrder =
    useRemoteFeatureFlags().isActive("implicitProforma") &&
    order.paymentType === "manual" &&
    !order.retailerPaidAt;

  const badges = (
    <>
      {showStatus && <OrderStatusIndicator iconOnly order={order} />}
      {showSellarCreditFeatures && sellarCreditIcon}
      {showPayment && !showSellarCreditFeatures && !isImplicitProformaOrder && (
        <PaymentStatusIndicator
          paymentStatus={order.paymentStatus}
          voidedAt={order.voidedAt}
          paidAt={order.paidAt}
          retailerPaidAt={order.retailerPaidAt}
          paymentType={order.paymentType}
          paymentDue={order.paymentDue}
          cancelledAt={order.cancelledAt}
          invoiceIssuedAt={order.invoiceIssuedAt}
          invoicesEnabled={order.Supplier.invoicesEnabled}
          iconOnly
        />
      )}
      {order.paymentType === "cardPaymentWithDeposit" && (
        <TooltipContainer enable text="Card payment with deposit">
          <BadgeZinc icon="DEPOSIT_PAYMENT" />
        </TooltipContainer>
      )}
      <AdminOnly>
        <>
          <OrderInitiatorIndicator initiator={order.initiator} />
          <OrderCheckoutSourceIndicator checkoutSource={order.checkoutSource} />
        </>
      </AdminOnly>
    </>
  );

  return (
    <OrderListElementLayout
      order={order}
      selected={selectedOrderId === order.id}
      businessName={order.Supplier.Business.name}
      logoUrl={order.Supplier.Business.logoUrl}
      badges={badges}
      date={<BuyerOrderDate order={order} />}
      showFulfillment={showFulfillment}
    />
  );
};

const BuyerOrderDate = ({ order }: { order: OrderListOrder }) => {
  if (order.fulfillmentType === "collection") {
    return (
      <span className="pl-4 text-right text-xs">
        Collection on: {formatOrderListDate(order.deliveryPlannedAt)}
      </span>
    );
  }

  if (!order.deliveryPlannedAt) return null;

  return (
    <span className="pl-4 text-right text-xs">
      Delivery on: {formatOrderListDate(order.deliveryPlannedAt)}
    </span>
  );
};
